import { ReactNode, createContext, useCallback, useContext, useMemo } from 'react';
import type { ContentAudience } from '../utils/audience';
import { fetchAllResourcePages } from '../utils/resourceListFetch';

/** Structural shape of the host's v2 client, so the package couples to no axios version. */
export interface ResourcesApiClient {
    get<T = unknown>(url: string): Promise<{ data: T }>;
    post<T = unknown>(
        url: string,
        body?: unknown,
        config?: { headers?: Record<string, string> }
    ): Promise<{ data: T }>;
    put<T = unknown>(url: string, body?: unknown): Promise<{ data: T }>;
    delete<T = unknown>(url: string): Promise<{ data: T }>;
}

export type ResourceKind = 'faqs' | 'videos' | 'helpDocuments';

/** v2 content endpoint paths. The resources section talks to v2 exclusively. */
export const V2_PATHS: Record<ResourceKind, string> = {
    faqs: '/content/faqs',
    videos: '/content/videos',
    helpDocuments: '/content/help-documents'
};

interface ResourcesConfig {
    /** v2 content client used to load resource lists and resolve file URLs. */
    api: ResourcesApiClient;
    /** Route prefix the resource links are mounted under (defaults to `/resources`). */
    basePath: string;
    /** Where the breadcrumb's home crumb points. Differs per SPA. */
    homePath: string;
    /** Appended to `document.title`; hosts that manage the tab title leave it unset. */
    documentTitleSuffix?: string;
    /** True when the host overlays controls top-right (dashboard avatar); pages keep that corner clear. */
    reserveTopRight: boolean;
    /** Mirrors the resources.update/resources.delete IAM permissions the write
     * routes enforce; both default false, so a silent host is read-only. */
    authoring: ResourceAuthoringAccess;
    /** Sent as `?audience=` on every list request; unset, the server decides by role. */
    audience?: ContentAudience;
}

export interface ResourceAuthoringAccess {
    canEdit: boolean;
    canDelete: boolean;
}

const NO_AUTHORING: ResourceAuthoringAccess = { canEdit: false, canDelete: false };

interface ResourcesConfigProviderProps {
    api: ResourcesApiClient;
    basePath?: string;
    homePath?: string;
    documentTitleSuffix?: string;
    reserveTopRight?: boolean;
    canEdit?: boolean;
    canDelete?: boolean;
    audience?: ContentAudience;
    children: ReactNode;
}

const ResourcesConfigContext = createContext<ResourcesConfig | undefined>(undefined);

// No pdfApiBaseUrl/proxy any more: resources files are GCS objects behind signed URLs.
export function ResourcesConfigProvider({
    api,
    basePath = '/resources',
    homePath = '/home',
    documentTitleSuffix,
    reserveTopRight = false,
    canEdit = false,
    canDelete = false,
    audience,
    children
}: ResourcesConfigProviderProps) {
    const authoring = useMemo<ResourceAuthoringAccess>(
        () => (canEdit || canDelete ? { canEdit, canDelete } : NO_AUTHORING),
        [canEdit, canDelete]
    );
    const value = useMemo<ResourcesConfig>(
        () => ({
            api,
            basePath,
            homePath,
            documentTitleSuffix,
            reserveTopRight,
            authoring,
            audience
        }),
        [api, basePath, homePath, documentTitleSuffix, reserveTopRight, authoring, audience]
    );
    return (
        <ResourcesConfigContext.Provider value={value}>{children}</ResourcesConfigContext.Provider>
    );
}

export function useResourcesConfig(): ResourcesConfig {
    const context = useContext(ResourcesConfigContext);
    if (context === undefined) {
        throw new Error('useResourcesConfig must be used within a ResourcesConfigProvider');
    }
    return context;
}

/** Loads a full v2 resource list, scoped to the host's audience when it sets one. */
export function useResourceList(): <T>(kind: ResourceKind) => Promise<T[]> {
    const { api, audience } = useResourcesConfig();
    return useCallback(
        <T,>(kind: ResourceKind): Promise<T[]> =>
            fetchAllResourcePages<T>(api, V2_PATHS[kind], audience),
        [api, audience]
    );
}
